import PropTypes from "prop-types";
import { useRef } from "react";

import styled from "styled-components/macro";
import { opacity } from "theme";

import { useManageMyGivingPage } from "Giving/ManageMyGivingPage/ManageMyGivingPageContext";
import { alertTypes } from "Giving/constants";
import { motion } from "framer-motion";

import FnrIcon from "UI/FnrIcon";

import useColorSimilarity from "Utilities/Colors/useColorSimilarity";
import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";
import useIsWiderThan from "Utilities/Hooks/useIsWiderThan";

import AlertButton from "./AlertButton";

export default function Alert({
  alert,
  index,
  dismissAlert,
  alertType,
  setActiveAlertID,
}) {
  const {
    state: {
      pageSettings: { accentColor },
    },
  } = useManageMyGivingPage();
  const containerRef = useRef();

  const isWiderThan = useIsWiderThan(containerRef, { px: 700 });
  const isAccentColorTooSimilarToPrimaryColor = useColorSimilarity(
    `#${accentColor}`,
    "#383838"
  );

  const isHoverFocused = useIsHoverFocused(containerRef);
  const animationVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.25,
        delay: index * 0.2,
        type: "tween",
      },
    },
    dismissed: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.25,
        type: "tween",
      },
    },
  };

  return (
    <AlertContainer
      ref={containerRef}
      key={alert.ID}
      data-cy={`alert-${index}`}
      variants={animationVariants}
      initial="hidden"
      animate="visible"
      exit="dismissed"
      $wide={isWiderThan}
    >
      <Left>
        <IconContainer>
          <FnrIcon icon={alert.icon} />
        </IconContainer>
        <TextContainer>
          <Title>{alert.title}</Title>
          <Description>
            {alert.description}
            {alert?.isDismissible && (
              <DismissButton
                onClick={() => {
                  dismissAlert(alert.ID);
                }}
                aria-label="close-alert"
                data-cy={`dismiss-alert-${index}`}
                $variant={isHoverFocused ? "shown" : "hidden"}
              >
                Dismiss
              </DismissButton>
            )}
          </Description>
        </TextContainer>
      </Left>

      <Right $wide={isWiderThan}>
        <AlertButton
          alertType={alertType}
          url={alert.actionButtonURL}
          color={
            isAccentColorTooSimilarToPrimaryColor ? "blue" : `#${accentColor}`
          }
          setActiveAlertID={setActiveAlertID}
        />
      </Right>
    </AlertContainer>
  );
}

const userPaymentMethodPropType = {
  ID: PropTypes.number,
  userID: PropTypes.number,
  paymentProviderID: PropTypes.number,
  fundItMerchantID: PropTypes.number,
  paymentMethodTypeID: PropTypes.number,
  nickname: PropTypes.string,
  last4: PropTypes.string,
  expirationMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  expirationYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  createdAt: PropTypes.number,
  deletedAt: PropTypes.number,
  becameInvalidAt: PropTypes.number,
  accountHolderName: PropTypes.string,
  zip: PropTypes.string,
  ipAddress: PropTypes.string,
};

const userRecurringPaymentsPropType = {
  ID: PropTypes.number,
  userRecurringPaymentStatusID: PropTypes.number,
  skippedUntil: PropTypes.number,
  recurringGiftUserID: PropTypes.number,
  userSavedPaymentMethodID: PropTypes.number,
  networkID: PropTypes.number,
  startDate: PropTypes.string,
  endDate: PropTypes.number,
  userRecurringPaymentIntervalID: PropTypes.number,
  monthNumberOfInterval: PropTypes.number,
  dayNumberOfInterval: PropTypes.number,
  failedPaymentsCount: PropTypes.number,
  applyToExistingPledge: PropTypes.bool,
  memo: PropTypes.string,
  nextPaymentDateCachedForDisplayOnly: PropTypes.string,
  createdAt: PropTypes.number,
  createdBy: PropTypes.number,
};
Alert.propTypes = {
  alert: PropTypes.shape({
    ID: PropTypes.number.isRequired,
    title: PropTypes.string,
    icon: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    isDismissible: PropTypes.bool.isRequired,
    dismissed: PropTypes.bool,
    actionButtonURL: PropTypes.string,
    givingAlertType: PropTypes.string,
    givingAlertTypeID: PropTypes.number,
    targetFundNames: PropTypes.arrayOf(PropTypes.string),
    userPaymentMethod: PropTypes.shape(userPaymentMethodPropType),
    userRecurringPayments: PropTypes.arrayOf(
      PropTypes.shape(userRecurringPaymentsPropType)
    ),
  }).isRequired,
  dismissAlert: PropTypes.func.isRequired,
  alertType: PropTypes.oneOf(Object.values(alertTypes)).isRequired,
  index: PropTypes.number.isRequired,
  setActiveAlertID: PropTypes.func.isRequired,
};

const AlertContainer = styled(motion.div)`
  grid-area: alerts;
  position: relative;
  display: flex;
  border-radius: 12px;
  background: linear-gradient(
    140deg,
    var(--bgGradient1) 0%,
    var(--bgGradient2) 100%
  );
  width: 100%;
  padding: 0.5rem;
  color: #fff;
  flex-direction: ${({ $wide }) => ($wide ? "row" : "column")};
  box-sizing: border-box;
`;

const Left = styled.div`
  flex: 70;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IconContainer = styled.div`
  width: 32px;
  min-width: 32px;
  font-size: 32px;
  margin: 0 1rem;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const Title = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
`;

const Description = styled.div`
  font-size: 1rem;
  font-weight: 500;
`;

const Right = styled.div`
  flex: 30;
  display: flex;
  justify-content: ${({ $wide }) => ($wide ? "flex-end" : "center")};
  margin: ${({ $wide }) => ($wide ? "0 0 0 1.5rem" : ".5rem")};
`;

const DismissButton = styled.button`
  background: none;
  color: inherit;
  border: none;
  cursor: pointer;
  ${opacity}
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
